import { FC } from 'react'

import { ONBOARDING_CARD } from './copyConstants'
import { Card, Text, CardColors, Image, Link } from '@/ui'
import { Colors } from '@/src/styles/theme'
import { TypographyStyles } from '@/styles'
import { getCurrentUser } from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'

const steps = [
  {
    number: 1,
    title: 'Mark personal transactions',
    instructions: (
      <>
        <li>1. For each personal transaction, go to the Type column.</li>
        <li>
          2. Choose <strong>Personal</strong> from the drop-down menu. This
          excludes the transaction from your business books.{'  '}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#risks_of_commingled_account"
            newPage
          >
            Why is this important?
          </Link>
        </li>
      </>
    ),
  },
  {
    number: 2,
    title: 'Provide context on unclear transactions',
    instructions: (
      <>
        <li>
          1. Click on the <strong>Needs Review</strong> tab to see all unclear
          transactions.
        </li>
        <li>
          2. Click the <strong>•••</strong> menu and add a note explaining what
          the transaction was used for.
        </li>
        <li>3. A bookkeeper will categorize the transaction accordingly.</li>
      </>
    ),
  },
]

const TransactionsOnboardingCard: FC = () => {
  const currentUser = useReselector(getCurrentUser)
  const userSpecificKey = currentUser?.id
    ? `${ONBOARDING_CARD.LOCAL_STORAGE_KEY}-user-${currentUser.id}`
    : ONBOARDING_CARD.LOCAL_STORAGE_KEY

  return (
    <Card
      color={CardColors.PRIORITY}
      className="border-blush flex"
      dismissible
      permanentDismissalKey={userSpecificKey}
    >
      <div className="flex gap-5">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Text variant={TypographyStyles.EYEBROW}>
              {ONBOARDING_CARD.SECTION_LABEL}
            </Text>
            <Text variant={TypographyStyles.H2}>{ONBOARDING_CARD.TITLE}</Text>
          </div>

          <Text variant={TypographyStyles.BODY_MD}>{ONBOARDING_CARD.BODY}</Text>

          {steps.map((step, index) => (
            <div
              key={step.title}
              style={{
                display: 'flex',
                gap: '12px',
                borderRadius: '8px',
                alignItems: 'flex-start',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  backgroundColor: Colors.moss,
                  color: Colors.black,
                  borderRadius: '50%',
                }}
              >
                {index + 1}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '4px',
                }}
              >
                <Text variant={TypographyStyles.H3}>{step.title}</Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  style={{ color: Colors.charcoal }}
                >
                  <ol>{step.instructions}</ol>
                </Text>
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            borderRadius: '16px',
            alignSelf: 'flex-end',
          }}
        >
          <Image
            src="https://heard-images.s3.amazonaws.com/assets/transaction-type-dropdown.png"
            alt=""
            className="object-contain rounded-lg"
          />
        </div>
      </div>
    </Card>
  )
}

export default TransactionsOnboardingCard
